import type { TFunction } from 'i18next'

import { EmptyPanel } from '@/components/feedback/empty-panel'
import { cn } from '@/lib/cn'

import type { AdminAppTransition } from './api'
import { formatDateTime, translateStatus } from './format'
import { AdminAppStatusCell } from './shared'

const lifecycleSteps = ['draft', 'testing', 'pending_publish', 'active'] as const

export function AdminAppLifecycleProgress({
  status,
  t,
}: {
  status: string
  t: TFunction<'adminApps'>
}) {
  const currentIndex = lifecycleSteps.findIndex((step) => step === status)

  return (
    <div aria-label={t('review.drawer.progressLabel')} className="grid gap-2">
      <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto pb-1">
        {lifecycleSteps.map((step, index) => {
          const isCurrent = step === status
          const isPassed = currentIndex >= 0 && index < currentIndex

          return (
            <div className="flex min-w-max items-center gap-1.5" key={step}>
              {index > 0 ? (
                <span
                  aria-hidden
                  className={cn(
                    'h-px w-8',
                    isPassed ? 'bg-[var(--color-primary)]' : 'bg-[rgba(26,28,25,0.12)]',
                  )}
                />
              ) : null}
              <span
                className={cn(
                  'inline-flex h-7 items-center rounded-full border px-2.5 text-xs font-medium',
                  isCurrent
                    ? 'border-[rgba(25,91,255,0.28)] bg-[rgba(25,91,255,0.1)] text-[var(--color-primary)]'
                    : isPassed
                      ? 'border-[#d9f7be] bg-[#f6ffed] text-[#16823c]'
                      : 'border-[var(--color-panel-border)] bg-white text-[var(--color-text-muted)]',
                )}
              >
                {translateStatus(t, step)}
              </span>
            </div>
          )
        })}
      </div>
      {status === 'banned' ? (
        <p className="text-xs leading-5 text-[var(--color-text-muted)]">
          {t('review.drawer.bannedOutsideFlow')}
        </p>
      ) : null}
    </div>
  )
}

export function AdminAppTransitionHistory({
  items,
  t,
}: {
  items: AdminAppTransition[]
  t: TFunction<'adminApps'>
}) {
  if (items.length === 0) {
    return <EmptyPanel className="py-5">{t('review.drawer.historyEmpty')}</EmptyPanel>
  }

  return (
    <div className="grid gap-2">
      {items.map((item) => (
        <article
          className="rounded-lg border border-[var(--color-panel-border)] bg-white px-4 py-3"
          key={item.id}
        >
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <AdminAppStatusCell
              status={item.from_status}
              text={translateStatus(t, item.from_status)}
            />
            <span className="text-[var(--color-text-muted)]">&rarr;</span>
            <AdminAppStatusCell status={item.to_status} text={translateStatus(t, item.to_status)} />
            <span className="text-[var(--color-text-muted)]">
              {t(`review.actions.${item.action}`)}
            </span>
          </div>
          <div className="mt-2 text-xs leading-5 text-[var(--color-text-muted)]">
            {item.performer_name || item.performed_by || t('review.drawer.unknownOperator')}
            {' · '}
            {item.created_at ? formatDateTime(item.created_at) : '-'}
          </div>
          {item.comment ? (
            <p className="mt-2 whitespace-pre-wrap text-sm leading-5 text-[var(--color-text-muted)]">
              {item.comment}
            </p>
          ) : null}
        </article>
      ))}
    </div>
  )
}
